/**
 * Single funnel for PowerSync diagnostics.
 *
 * Sync problems are invisible by default: they happen off the render path, inside a retry loop,
 * and a stalled CRUD queue looks identical to "nothing to upload". Routing every message through
 * here keeps the `noConsole` exemption in one file and gives one place to attach a reporting
 * transport — see `setPowersyncLogTransport`.
 */

import { LogLevels, type PowerSyncLogger } from '@powersync/common';
import { isRoutineSyncTokenExpiry } from './expiredSyncToken';

const PREFIX = '[PowerSync]';

const CONSOLE_METHOD = {
  info: 'log',
  warning: 'warn',
  error: 'error',
} as const;

type LogContext = Record<string, unknown>;

export type PowersyncLogLevel = keyof typeof CONSOLE_METHOD;

export type PowersyncLogEvent = {
  level: PowersyncLogLevel;
  message: string;
  context?: LogContext;
  /**
   * The original thrown value, when the log came from a `catch`. Deliberately left unflattened so
   * a transport can hand a real Error — and therefore a real stack trace — to its reporter.
   */
  error?: unknown;
};

export type PowersyncLogTransport = (event: PowersyncLogEvent) => void;

let transport: PowersyncLogTransport | null = null;

/**
 * Installs a sink for PowerSync diagnostics, in addition to the console.
 *
 * This package cannot import a Sentry SDK itself — mobile ships `@sentry/react-native` and the
 * dashboard ships `@sentry/react` — so each app injects its own at startup via
 * `createSentryLogTransport`. Pass `null` to remove it.
 */
export const setPowersyncLogTransport = (next: PowersyncLogTransport | null) => {
  transport = next;
};

/**
 * Flattens an unknown thrown value into loggable fields, including the axios response shape the
 * Laravel upload path throws. Never include the request config — it carries the bearer token.
 */
export const describeError = (error: unknown): LogContext => {
  if (typeof error !== 'object' || error === null) {
    return { error: String(error) };
  }

  const candidate = error as {
    message?: string;
    code?: string;
    response?: { status?: number; data?: unknown };
  };

  return {
    message: candidate.message,
    code: candidate.code,
    status: candidate.response?.status,
    response: candidate.response?.data,
  };
};

const emit = (level: PowersyncLogLevel, message: string, context?: LogContext, error?: unknown) => {
  // biome-ignore lint/suspicious/noConsole: this module is the intended sink for PowerSync logs
  console[CONSOLE_METHOD[level]](`${PREFIX} ${message}`, context ?? '');

  if (!transport) {
    return;
  }

  try {
    transport({ level, message, context, error });
  } catch {
    // A failing transport must never propagate into the sync or upload path — losing a report is
    // recoverable, throwing here would abort a transaction or a connection attempt.
  }
};

export const powersyncLogger = {
  info: (message: string, context?: LogContext) => emit('info', message, context),
  warn: (message: string, context?: LogContext) => emit('warning', message, context),
  error: (message: string, context?: LogContext) => emit('error', message, context),
  /**
   * For a `catch` block. The flattened fields go to the console line while the original value is
   * forwarded untouched, so the transport can report it with its stack intact.
   */
  exception: (message: string, error: unknown, context?: LogContext) =>
    emit('error', message, { ...describeError(error), ...context }, error),
};

/**
 * The SDK's own diagnostics, routed into this module.
 *
 * `@powersync/common` logs through a `PowerSyncLogger` it creates itself unless one is passed to
 * the database constructor, which meant its messages went straight to `console` — outside the
 * transport, and invisible to Sentry. They are also the only narration of what the sync stream is
 * actually doing; our own logs come from `statusChanged`, which reports the result and not the step
 * that stalled.
 *
 * `debug` rather than `info`, because the messages worth having when a connection hangs — whether
 * the stream response was ever established, what the server answered — sit below `info`. `trace` is
 * per-line and would bury everything else.
 */
export const sdkLogger: PowerSyncLogger = {
  log: ({ level, message, error }) => {
    if (level < LogLevels.debug) {
      return;
    }

    // The SDK logs a refused sync token as an error, and recovers from it on its next attempt.
    if (level >= LogLevels.error && isRoutineSyncTokenExpiry(error)) {
      emit('warning', `sdk: ${message}`, describeError(error));
      return;
    }

    if (level >= LogLevels.error) {
      emit('error', `sdk: ${message}`, error ? describeError(error) : undefined, error);
      return;
    }

    emit(level >= LogLevels.warn ? 'warning' : 'info', `sdk: ${message}`, error ? describeError(error) : undefined);
  },
};
